import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import apiService from '../../context/apiService.js';
import { toast } from 'react-toastify';
import { 
  Search, 
  Filter, 
  Star, 
  Clock, 
  Users,
  BookOpen,
  Play,
  Loader2,
  CheckCircle
} from 'lucide-react';

const StudentCourses = () => {
  const { userData } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState({});
  const [lessonCounts, setLessonCounts] = useState({});

  // Fetch all published courses
  const fetchCourses = async () => {
    try {
      const response = await apiService.courses.getAllPublic();
      if (response.data.success) {
        setCourses(response.data.courses);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  // Fetch enrolled courses to check enrollment status
  const fetchEnrolledCourses = async () => {
    try {
      const response = await apiService.enrollment.getEnrolledCourses();
      if (response.data.success) {
        // Handle both old and new API response format
        const enrollments = response.data.enrollments || response.data.courses;
        setEnrolledCourses(enrollments);
      }
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      // Don't show error toast for this as it's not critical
    }
  };

  // Fetch lesson counts for all courses
  const fetchLessonCounts = async (coursesData) => {
    const counts = {};
    for (const course of coursesData) {
      try {
        const response = await apiService.lessons.getByCourse(course._id);
        counts[course._id] = response?.data?.count || 0;
      } catch (error) {
        counts[course._id] = 0;
      }
    }
    setLessonCounts(counts);
  };

  // Fetch all data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      await fetchCourses();
      await fetchEnrolledCourses();
      // Fetch lesson counts after courses are loaded
      if (courses.length > 0) {
        await fetchLessonCounts(courses);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Enroll in a course
  const handleEnroll = async (courseId) => {
    setEnrolling(prev => ({ ...prev, [courseId]: true }));
    try {
      const response = await apiService.enrollment.enroll(courseId);
      if (response.data.success) {
        toast.success('Successfully enrolled in course!');
        // Refresh enrolled courses
        await fetchEnrolledCourses();
      } else {
        toast.error(response.data.message || 'Failed to enroll in course');
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
      toast.error('Failed to enroll in course');
    } finally {
      setEnrolling(prev => ({ ...prev, [courseId]: false }));
    }
  };

  useEffect(() => {
    if (userData && userData.role === 'student') {
      fetchAllData();
    }
  }, [userData]);

  // Fetch lesson counts when courses change
  useEffect(() => {
    if (courses.length > 0) {
      fetchLessonCounts(courses);
    }
  }, [courses]);

  // Get unique categories from courses
  const getUniqueCategories = () => {
    const categorySet = new Set(courses.map(course => course.category).filter(Boolean));
    const categoryOptions = Array.from(categorySet).map(category => ({
      value: category,
      label: category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')
    }));
    
    return [
      { value: 'all', label: 'All Categories' },
      ...categoryOptions
    ];
  };

  const categories = getUniqueCategories();

  const levels = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'newest', label: 'Newest' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' }
  ];

  // Check if course is enrolled and get enrollment details
  const getEnrollmentDetails = (courseId) => {
    return enrolledCourses.find(enrollment => {
      const course = enrollment.course || enrollment;
      return course._id === courseId;
    });
  };

  // Check if course is enrolled
  const isCourseEnrolled = (courseId) => {
    return getEnrollmentDetails(courseId) !== undefined;
  };

  // Format enrollment date
  const formatEnrollmentDate = (enrollment) => {
    const enrolledAt = enrollment.enrolledAt || enrollment.createdAt || enrollment.updatedAt;
    if (!enrolledAt) return 'Recently';
    
    const date = new Date(enrolledAt);
    if (isNaN(date.getTime())) return 'Recently';
    
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter and sort courses
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.creater?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    // Note: Level filtering might not be available in backend data, so we'll skip it for now
    const matchesLevel = selectedLevel === 'all'; // Always true for now
    
    return matchesSearch && matchesCategory && matchesLevel;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'popular':
      default:
        // Sort by enrollment count or created date as fallback
        return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-700';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading courses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Browse Courses</h1>
          <p className="text-secondary-600 mt-1">Discover amazing courses and start your learning journey</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchAllData}
          disabled={loading}
          className="flex items-center space-x-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : null}
          <span>Refresh</span>
        </Button>
      </div>

      {/* Search and Filters */}
      <Card padding="sm">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
            <input
              type="text"
              placeholder="Search courses, instructors, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-secondary-600" />
              <span className="text-sm font-medium text-secondary-700">Filters:</span>
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input w-48"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="input w-40"
            >
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input w-48"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-600">
          Showing {filteredCourses.length} of {courses.length} courses
        </p>
      </div>

      {/* Scrollable Courses Area */}
      <div className="overflow-y-auto overscroll-contain pr-1 max-h-[calc(100vh-240px)]">
        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredCourses.map((course) => {
            const enrollmentDetails = getEnrollmentDetails(course._id);
            const isEnrolled = isCourseEnrolled(course._id);
            const isCurrentlyEnrolling = enrolling[course._id];
            const enrollmentDate = enrollmentDetails ? formatEnrollmentDate(enrollmentDetails) : null;
            
            return (
              <Card key={course._id} className="overflow-hidden course-card-hover flex flex-col" style={{ minHeight: '440px' }}>
                <div className="aspect-video bg-gray-200 relative flex-shrink-0 course-thumbnail">
                  <img 
                    src={course.thumbnail || 'https://via.placeholder.com/300x200?text=Course'} 
                    alt={course.title}
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 text-xs font-medium rounded-full">
                      {course.category || 'Course'}
                    </span>
                  </div>
                  {isEnrolled ? (
                    <div className="absolute top-2 right-2">
                      <span className="bg-green-500 text-white px-2 py-1 text-xs font-medium rounded">
                        Enrolled
                      </span>
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2">
                      <span className="bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium rounded-full text-secondary-700 shadow-sm">
                        <BookOpen size={12} className="inline mr-1" />
                        {lessonCounts[course._id] !== undefined ? lessonCounts[course._id] : '...'} lessons
                      </span>
                    </div>
                  )}
                  {enrollmentDate && (
                    <div className="absolute bottom-2 left-2">
                      <span className="bg-black bg-opacity-70 text-white px-2 py-1 text-xs font-medium rounded">
                        Enrolled {enrollmentDate}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <Link to={`/student/courses/${course._id}`} className="opacity-0 hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-3">
                      <Play size={24} className="text-primary-600" />
                    </Link>
                  </div>
                </div>
                
                <div className="pt-4 px-4 pb-4 flex flex-col flex-1">
                  <h3 
                    className="font-semibold text-secondary-900 mb-1 line-clamp-2 min-h-[3rem] cursor-pointer text-hover-primary transition-colors"
                    title={course.title}
                  >
                    {course.title}
                  </h3>
                  <p className="text-xs text-secondary-500 mb-2">by {course.creater?.name || 'Unknown Instructor'}</p>
                  <p 
                    className="text-sm text-secondary-600 mb-3 line-clamp-2 min-h-[2.5rem] cursor-pointer text-hover-secondary transition-colors"
                    title={course.description}
                  >
                    {course.description}
                  </p>
                  
                  <div className="flex items-center space-x-4 text-sm text-secondary-600 mb-2">
                    <span className="flex items-center">
                      <Star size={14} className="mr-1 text-yellow-500 fill-current" />
                      {course.rating || '4.5'}
                    </span>
                    <span className="flex items-center">
                      <Users size={14} className="mr-1" />
                      {course.studentEnroll?.length || 0}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-primary-600">₹{course.price || '99'}</span>
                    {isEnrolled && (
                      <span className="text-xs text-green-600 font-medium flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        Enrolled
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-auto">
                    {isEnrolled ? (
                      <Link to={`/student/courses/${course._id}`}>
                        <Button className="w-full">
                          <BookOpen size={16} className="mr-2" />
                          Continue Learning
                        </Button>
                      </Link>
                    ) : (
                      <Button 
                        className="w-full"
                        onClick={() => handleEnroll(course._id)}
                        disabled={isCurrentlyEnrolling}
                      >
                        {isCurrentlyEnrolling ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Enrolling...
                          </>
                        ) : (
                          'Enroll Now'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {filteredCourses.length === 0 && (
        <Card className="text-center py-12">
          <BookOpen size={48} className="mx-auto text-secondary-400 mb-4" />
          <h3 className="text-lg font-semibold text-secondary-900 mb-2">No courses found</h3>
          <p className="text-secondary-600 mb-6">
            Try adjusting your search or filter criteria to find more courses
          </p>
          <Button onClick={() => {
            setSearchQuery('');
            setSelectedCategory('all');
            setSelectedLevel('all');
          }}>
            Clear Filters
          </Button>
        </Card>
      )}
    </div>
  );
};

export default StudentCourses;
